import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, GraduationCap, BookOpen, CalendarCheck, Euro, BellRing,
  ClipboardCheck, Ticket, ShieldCheck, TrendingUp, CalendarDays, UserCog, Search, ArrowLeft, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OR = "#E2621B";

const NAV = [
  ["Dashboard", "/cockpit-vorschau", LayoutDashboard], ["Teilnehmende", "/teilnehmer", Users],
  ["Kunden", "/pflegeeinrichtungen", Building2], ["Maßnahmen", "/massnahmen", GraduationCap],
  ["Klassenbuch", "/klassenbuch-neu", BookOpen], ["Anwesenheit", "/anwesenheit", CalendarCheck],
  ["Rechnungen", "/rechnungen", Euro], ["Mahnwesen", "/rechnungen", BellRing],
  ["Prüfungen", "/pruefungen", ClipboardCheck], ["Bildungsgutscheine", "/bildungsgutscheine", Ticket],
  ["AZAV & QM", "/azav-ordner", ShieldCheck], ["Vertrieb", "/vertrieb", TrendingUp],
  ["Kalender", "/kalender", CalendarDays], ["Personal", "/personalakten", UserCog],
];

const eur = (v) => `${Math.round(v || 0).toLocaleString("de-DE")} €`;
const d10 = (v) => (v ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : "—");
const AMPEL = { gruen: ["#2F9E44", "vollständig"], gelb: ["#F08C00", "offen"], rot: ["#E03131", "offen"] };

export const Nav = ({ aktiv = 0 }) => (
  <aside className="hidden md:flex w-60 shrink-0 bg-white border-r border-[#ECEAE6] flex-col" data-testid="cockpit-nav">
    <div className="px-5 py-5 flex items-center gap-2">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: OR }}>D</div>
      <div className="leading-tight"><p className="font-bold text-[15px] text-[#1F2328]">Akademie Cockpit</p><p className="text-[11px] text-[#8A8580]">Dickies-Campus</p></div>
    </div>
    <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
      {NAV.map(([label, to, Icon], i) => (
        <Link key={label} to={to} data-testid={`cockpit-nav-${i}`}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[14px] transition-colors ${i === aktiv ? "font-semibold" : "text-[#4A4642] hover:bg-[#F7F6F4]"}`}
          style={i === aktiv ? { background: "#FDEFE6", color: OR } : undefined}>
          <Icon size={17} />{label}
        </Link>
      ))}
    </nav>
    <Link to="/" className="m-3 flex items-center gap-2 text-xs text-[#8A8580] hover:text-[#1F2328]" data-testid="cockpit-zurueck"><ArrowLeft size={14} />Zurück zum aktuellen CRM</Link>
  </aside>
);

const Kachel = ({ wert, label, sub, testid, warn }) => (
  <div className="bg-white rounded-2xl border border-[#ECEAE6] p-5" data-testid={testid}>
    <p className="text-[34px] font-bold leading-none" style={{ color: warn ? OR : "#1F2328" }}>{wert ?? "…"}</p>
    <p className="mt-2 text-sm font-medium text-[#1F2328]">{label}</p>
    {sub && <p className="text-xs text-[#8A8580] mt-0.5">{sub}</p>}
  </div>
);

const MassnahmenTabelle = ({ rows }) => (
  <div className="bg-white rounded-2xl border border-[#ECEAE6] overflow-x-auto" data-testid="cockpit-massnahmen">
    <p className="px-5 pt-4 pb-3 font-semibold text-[#1F2328]">Prüfungen & Zertifikate je Maßnahme</p>
    <table className="w-full text-sm">
      <thead className="bg-[#F7F6F4] text-[#8A8580] text-xs"><tr>
        {["Maßnahme", "MN-Nr.", "Aktiv", "Bestanden", "Ohne Prüfungs-Upload", "Ohne Zertifikat", "Ampel"].map((h) => <th key={h} className="text-left font-medium px-5 py-2">{h}</th>)}
      </tr></thead>
      <tbody>{rows.map((m, i) => {
        const [c, t] = AMPEL[m.ampel];
        return (
          <tr key={m.kurs} className="border-t border-[#F0EEEA]" data-testid={`cockpit-mn-row-${i}`}>
            <td className="px-5 py-2.5 text-[#1F2328]">{m.kurs}</td><td className="px-5 text-[#8A8580]">{m.azav_nr ? `364 ${m.azav_nr}` : "privat"}</td>
            <td className="px-5">{m.aktiv}</td><td className="px-5">{m.bestanden}</td><td className="px-5">{m.ohne_pruefung}</td><td className="px-5">{m.ohne_zertifikat}</td>
            <td className="px-5"><span className="inline-flex items-center gap-1.5 text-xs" style={{ color: c }}><span className="w-2 h-2 rounded-full" style={{ background: c }} />{m.ampel === "gruen" ? t : `${m.ohne_pruefung + m.ohne_zertifikat} ${t}`}</span></td>
          </tr>
        );
      })}</tbody>
    </table>
  </div>
);

const OffenListe = ({ titel, rows, testid }) => (
  <div className="bg-white rounded-2xl border border-[#ECEAE6] p-5 min-w-0" data-testid={testid}>
    <p className="font-semibold text-[#1F2328] mb-3">{titel} <span className="text-[#8A8580] font-normal">({rows.length})</span></p>
    <div className="space-y-1 max-h-72 overflow-y-auto">
      {rows.length === 0 && <p className="text-sm text-[#8A8580]">Nichts offen.</p>}
      {rows.map((r) => (
        <Link key={`${r.teilnehmer_id}-${r.kurs}`} to={`/teilnehmer?id=${r.teilnehmer_id}`} className="flex justify-between gap-3 text-sm py-1.5 px-2 rounded hover:bg-[#F7F6F4] min-w-0">
          <span className="text-[#1F2328] truncate min-w-0">{r.name}<span className="text-[#8A8580]"> · {r.kurs}</span></span><span className="text-[#8A8580] shrink-0">{d10(r.ende)}</span>
        </Link>
      ))}
    </div>
  </div>
);

export default function CockpitVorschau() {
  const [d, setD] = useState(null);
  const [offen, setOffen] = useState(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    axios.get(`${API}/cockpit/kennzahlen`).then((r) => setD(r.data)).catch(() => setFehler(true));
    axios.get(`${API}/rechnungen/statistik`).then((r) => setOffen(r.data)).catch(() => {});
  }, []);
  const k = d?.kacheln || {};
  const std = new Date().getHours();
  return (
    <div className="fixed inset-0 z-40 flex bg-[#F7F6F4]" style={{ fontFamily: "'Manrope', 'Segoe UI', sans-serif" }} data-testid="cockpit-vorschau">
      <Nav />
      <main className="flex-1 overflow-y-auto">
        <header className="h-16 bg-white border-b border-[#ECEAE6] flex items-center pl-8 pr-20 gap-4">
          <div className="flex items-center gap-2 bg-[#F7F6F4] rounded-lg px-3 py-2 w-96 max-w-full text-sm truncate text-[#8A8580]"><Search size={16} />Teilnehmende, Einrichtungen, Rechnungen suchen …</div>
          <span className="ml-auto text-xs px-2 py-1 rounded-full hidden sm:inline" style={{ background: "#FDEFE6", color: OR }}>Vorschau – Ihr CRM bleibt unverändert</span>
        </header>
        <div className="p-4 md:p-8 space-y-6 max-w-[1400px]">
          <div><h1 className="text-[28px] font-bold text-[#1F2328]" style={{ fontFamily: "inherit" }}>{std < 11 ? "Guten Morgen" : std < 18 ? "Guten Tag" : "Guten Abend"}.</h1>
            <p className="text-sm text-[#8A8580]">Stand {d ? new Date(d.stand).toLocaleString("de-DE") : "…"}</p></div>
          {fehler && <p className="text-sm text-red-600" data-testid="cockpit-fehler">Kennzahlen konnten nicht geladen werden.</p>}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kachel wert={k.aktive_tn} label="aktive Teilnehmende" testid="kachel-aktive-tn" />
            <Kachel wert={k.laufende_massnahmen} label="laufende Maßnahmen" testid="kachel-laufende-mn" />
            <Kachel wert={offen ? eur(offen.offene_summe) : null} label="offene Forderungen" sub={offen ? `${offen.ueberfaellig} überfällig` : ""} testid="kachel-forderungen" />
            <Kachel wert={k.bestanden_ohne_pruefung} label="bestanden, Prüfung nicht hochgeladen" testid="kachel-ohne-pruefung" warn={k.bestanden_ohne_pruefung > 0} />
            <Kachel wert={k.ohne_zertifikat} label="bestanden, ohne Zertifikat" testid="kachel-ohne-zertifikat" warn={k.ohne_zertifikat > 0} />
          </div>
          {d && <MassnahmenTabelle rows={d.massnahmen} />}
          {d && <div className="grid lg:grid-cols-2 gap-4">
            <OffenListe titel="Prüfung nicht hochgeladen" rows={d.ohne_pruefung} testid="liste-ohne-pruefung" />
            <OffenListe titel="Ohne Zertifikat" rows={d.ohne_zertifikat} testid="liste-ohne-zertifikat" />
          </div>}
          {d && <p className="text-xs text-[#8A8580] flex gap-2" data-testid="cockpit-regel"><Info size={14} className="shrink-0 mt-0.5" />So wird gezählt: {d.regel}</p>}
        </div>
      </main>
    </div>
  );
}
